import React from "react";
import { Upload, Button, Alert, Flex, Space, Typography } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  CheckCircleFilled,
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { downloadTargetMemberTemplate } from "../../utils/targetMember";

const { Text } = Typography;

interface TargetMemberUploadProps {
  fileList: UploadFile[];
  memberCount: number;
  parsing: boolean;
  /** True while the uploaded list is being checked against registered members. */
  checking?: boolean;
  /** Count of uploaded members matched to a registered account, once checked. */
  matchedCount?: number;
  beforeUpload: (file: File) => boolean | Promise<void> | any;
  onRemove: () => void;
  onPreview: () => void;
}

/**
 * UI for the "Uploaded Member List" campaign template — lets the user upload a
 * fixed target audience instead of configuring RFM criteria. Purely
 * presentational — all parsing/state/backend handling stays in the parent and
 * is passed in via props. Mirrors ExcludeMemberUpload's UX.
 */
const TargetMemberUpload: React.FC<TargetMemberUploadProps> = ({
  fileList,
  memberCount,
  parsing,
  checking,
  matchedCount,
  beforeUpload,
  onRemove,
  onPreview,
}) => {
  const hasFile = fileList.length > 0;
  const fileName = fileList[0]?.name ?? "Target member list";

  const uploadTrigger = (label: string) => (
    <Upload
      accept=".xlsx,.xls"
      maxCount={1}
      fileList={fileList}
      beforeUpload={beforeUpload}
      onRemove={onRemove}
      showUploadList={false}
    >
      <Button
        type="primary"
        icon={label === "Choose File" ? <UploadOutlined /> : undefined}
        loading={parsing}
        disabled={parsing}
      >
        {label}
      </Button>
    </Upload>
  );

  if (!hasFile) {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Flex align="center" gap={12}>
          {uploadTrigger("Choose File")}
          <Text type="secondary">No file chosen</Text>
        </Flex>
        <Text type="secondary">Upload member list (XLSX only)</Text>
        <Alert
          type="info"
          showIcon
          message={
            <span>
              Use the provided template to ensure the correct file format.{" "}
              <Button
                type="link"
                style={{ padding: 0, height: "auto" }}
                onClick={downloadTargetMemberTemplate}
              >
                Download Template
              </Button>
            </span>
          }
        />
      </Space>
    );
  }

  const notFoundCount =
    matchedCount !== undefined ? memberCount - matchedCount : 0;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12 }}
      >
        <Flex align="center" gap={10} style={{ minWidth: 0 }}>
          <CheckCircleFilled style={{ color: "#12B76A", fontSize: 22 }} />
          <div style={{ minWidth: 0 }}>
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              File Selected:
            </Text>
            <Text strong ellipsis style={{ display: "block" }}>
              {fileName}
            </Text>
          </div>
        </Flex>
        <Flex align="center" gap={8}>
          <Button
            type="text"
            icon={<DeleteOutlined style={{ color: "#DC2626" }} />}
            onClick={onRemove}
          />
          {uploadTrigger("Change")}
        </Flex>
      </Flex>

      {memberCount > 0 && checking && (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined />}
          message="Checking uploaded members against registered accounts..."
        />
      )}

      {memberCount > 0 && !checking && (
        <Alert
          type="info"
          showIcon
          icon={
            <img
              src="/icon/icon-memberlist.svg"
              alt="target members"
              style={{ width: 40, height: 40 }}
            />
          }
          message={
            <Flex align="center" justify="space-between" gap={12}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {matchedCount ?? memberCount} Target Members Found
                </div>
                <div style={{ fontSize: 13, color: "#595959" }}>
                  {matchedCount === undefined || matchedCount === memberCount
                    ? "These members are selected for this campaign."
                    : matchedCount === 0
                      ? "None of the uploaded members were found."
                      : `Matched ${matchedCount} of ${memberCount} uploaded records.`}
                </div>
              </div>
              <Button type="primary" icon={<EyeOutlined />} onClick={onPreview}>
                View Members
              </Button>
            </Flex>
          }
        />
      )}

      {memberCount > 0 && !checking && notFoundCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            matchedCount === 0
              ? "No target members were found. Please review your uploaded file."
              : `${notFoundCount} uploaded members were not found and will not receive this campaign.`
          }
        />
      )}
    </Space>
  );
};

export default TargetMemberUpload;
